import { useToastContext } from '@rtkwlf/fenrir-react';
import type {
  SpidrWithFocus,
  UpdateOrganizationSpidrRatingRequest,
} from '@rtkwlf/track-public';
import type { UpdateOrganizationSpidrImprovementsRequest } from '@rtkwlf/track-public';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type TFunction } from 'i18next';

import { useTrackApi } from './api-provider';
import { queryKeys } from './query-keys';
import {
  useGetOrganizationSpidrBlueprintsOptions,
  useGetOrganizationSpidrOptions,
} from './query-options';

/**
 * Favourites a blueprint for a customer
 *
 * /api/v1/organizations/{orgID}/spidr-blueprints/{spidrBlueprintID}/favourites
 */
export const useCreateSpidrBlueprintFavorite = (
  spidrBlueprintID: string,
  t: TFunction<'security-journey'>,
) => {
  const { client, orgID } = useTrackApi();
  const { addToast } = useToastContext();

  const queryClient = useQueryClient();
  const queryKey = useGetOrganizationSpidrBlueprintsOptions().queryKey;

  return useMutation({
    mutationFn: () =>
      client.createSpidrBlueprintFavourite.bind(client)({
        orgID,
        spidrBlueprintID,
      }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });

      const previousBlueprints = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => {
        if (old) {
          const blueprintToUpdate = old.blueprints[spidrBlueprintID];

          if (blueprintToUpdate == null) {
            return old;
          }

          blueprintToUpdate.isFavourited = true;

          const newBlueprints = structuredClone(old.blueprints);
          newBlueprints[spidrBlueprintID] = blueprintToUpdate;

          return {
            ...old,
            blueprints: newBlueprints,
          };
        }
        return old;
      });

      // Return a context object with the snapshotted value
      return { previousBlueprints };
    },
    /**
     * Purposefully commenting this out to prevent rows in the table from
     * jumping when a user favorites a blueprint. There is a chance the UI is out of date if two users
     * favorite the same blueprint in a 10 minute window.
     */
    // onSettled: async () => {
    //   return await queryClient.invalidateQueries({ queryKey });
    // },
    onError: (_, __, context) => {
      queryClient.setQueryData(queryKey, context?.previousBlueprints);
      addToast({
        appearance: 'danger',
        duration: 5,
        message: t('spidrLibrary.favorites.created.description'),
        title: t('spidrLibrary.favorites.created.title'),
      });
    },
  });
};

/**
 * Removes a favourite blueprint for a customer
 *
 * /api/v1/organizations/{orgID}/spidr-blueprints/{spidrBlueprintID}/favourites
 */
export const useDeleteBlueprintFavorite = (
  spidrBlueprintID: string,
  t: TFunction<'security-journey'>,
) => {
  const { client, orgID } = useTrackApi();
  const { addToast } = useToastContext();

  const queryClient = useQueryClient();
  const queryKey = useGetOrganizationSpidrBlueprintsOptions().queryKey;

  return useMutation({
    mutationFn: () =>
      client.deleteSpidrBlueprintFavourite.bind(client)({
        orgID,
        spidrBlueprintID,
      }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });

      const previousBlueprints = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => {
        if (old) {
          const blueprintToUpdate = old.blueprints[spidrBlueprintID];

          if (blueprintToUpdate == null) {
            return old;
          }

          blueprintToUpdate.isFavourited = false;

          const newBlueprints = structuredClone(old.blueprints);
          newBlueprints[spidrBlueprintID] = blueprintToUpdate;

          return {
            ...old,
            blueprints: newBlueprints,
          };
        }
        return old;
      });

      return { previousBlueprints };
    },
    /**
     * Purposefully commenting this out to prevent rows in the table from
     * jumping when a user favorites a blueprint. There is a chance the UI is out of date if two users
     * favorite the same blueprint in a 10 minute window.
     */
    // onSettled: async () => {
    //   return await queryClient.invalidateQueries({ queryKey });
    // },
    onError: (_, __, context) => {
      queryClient.setQueryData(queryKey, context?.previousBlueprints);
      addToast({
        appearance: 'danger',
        duration: 5,
        message: t('spidrLibrary.favorites.deleted.description'),
        title: t('spidrLibrary.favorites.deleted.title'),
      });
    },
  });
};

/**
 * Updates rating for a spidr
 *
 * /api/v1/organizations/{orgID}/spidrs/{spidrID}/rating
 */
export const useUpdateOrganizationSpidrRating = (
  spidrID: string,
  t: TFunction<'security-journey'>,
) => {
  const { client, pod, orgID } = useTrackApi();
  const queryClient = useQueryClient();

  const getSpidrKey = queryKeys.organizations.id.spidrs.id.index(pod, {
    orgID,
    spidrID,
  });

  return useMutation({
    mutationFn: (params: UpdateOrganizationSpidrRatingRequest) =>
      client.updateOrganizationSpidrRating.bind(client)(params),
    onMutate: async (params: UpdateOrganizationSpidrRatingRequest) => {
      await queryClient.cancelQueries({ queryKey: getSpidrKey });
      const oldSpidrWithFocus: SpidrWithFocus | undefined =
        queryClient.getQueryData(getSpidrKey);

      queryClient.setQueryData(getSpidrKey, (old: SpidrWithFocus) => ({
        ...old,
        rating: params.spidrRatingInput.rating,
        version: old.version + 1,
      }));

      return { oldSpidrWithFocus };
    },
    onError: (err, _, context) => {
      queryClient.setQueryData(getSpidrKey, context?.oldSpidrWithFocus);
    },
    onSuccess: (updatedSpidr) => {
      queryClient.setQueryData(getSpidrKey, updatedSpidr);
    },
  });
};

/**
 * Updates improvements for a spidr
 *
 * /api/v1/organizations/{orgID}/spidrs/{spidrID}/improvements
 */
export const useUpdateOrganizationSpidrImprovements = (
  spidrID: string,
  t: TFunction<'security-journey'>,
) => {
  const { client } = useTrackApi();

  const queryClient = useQueryClient();
  const queryKey = useGetOrganizationSpidrOptions({ spidrID }).queryKey;

  return useMutation({
    mutationFn: (params: UpdateOrganizationSpidrImprovementsRequest) =>
      client.updateOrganizationSpidrImprovements.bind(client)(params),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
    },
  });
};
